/**
 * Merge a node's incoming `data` payload over its kind-specific default,
 * so every field is guaranteed present at the design-system default
 * value. Used at the top of each node Component to eliminate inline
 * `?? fallback` noise and to relieve upstream writers (the meeting-canvas
 * bridge, legacy doc hydrators, fixtures) from having to enumerate every
 * field.
 *
 * Contract: defaults provide the floor; `data` overrides per-field. Pass
 * `data` from React Flow's `NodeProps.data` (typed loosely as unknown
 * since RF widens it). The result is the strict component-facing shape.
 *
 * Why a helper instead of inline spreads in each component: the merge
 * happens in five places (Shape, Sticky, Frame, Freedraw, Text) and the
 * cast pattern is identical. Centralising avoids drift if e.g. someone
 * forgets the `?? {}` guard or types the cast wrong.
 */
export function withDefaults<T extends object>(
  data: unknown,
  makeDefault: () => T,
): T {
  return { ...makeDefault(), ...((data ?? {}) as Partial<T>) } as T;
}
